import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Target, Plus, RefreshCw, Trash2, CheckCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const GOAL_TYPES = [
  { value: 'revenue', label: 'Receita (R$)', unit: 'R$', icon: '💰' },
  { value: 'patients', label: 'Pacientes Ativos', unit: 'pacientes', icon: '👥' },
  { value: 'appointments', label: 'Consultas no Mês', unit: 'consultas', icon: '📅' },
  { value: 'weight_loss', label: 'Perda de Peso Média (kg)', unit: 'kg', icon: '⚖️' },
  { value: 'custom', label: 'Meta Personalizada', unit: '', icon: '🎯' },
];

export const GoalsDashboard = () => {
  const [goals, setGoals] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'revenue', target_value: '', unit: '' });

  const fetchGoals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/goals?month=${currentMonth}`, { withCredentials: true });
      setGoals(data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [currentMonth]);

  useEffect(() => { fetchGoals(); }, [fetchGoals]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const goalType = GOAL_TYPES.find(t => t.value === form.type);
    try {
      await axios.post(`${API_URL}/api/goals`, {
        month: currentMonth,
        title: form.title,
        type: form.type,
        target_value: parseFloat(form.target_value),
        unit: form.unit || goalType?.unit || ''
      }, { withCredentials: true });
      toast.success('Meta criada!');
      setIsCreateOpen(false);
      setForm({ title: '', type: 'revenue', target_value: '', unit: '' });
      fetchGoals();
    } catch (error) {
      toast.error('Erro ao criar meta');
    }
  };

  const handleAutoUpdate = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/api/goals/auto-update`, {}, { withCredentials: true });
      toast.success('Metas atualizadas com dados reais!');
      fetchGoals();
    } catch (error) {
      toast.error('Erro ao atualizar');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Excluir esta meta?')) return;
    try {
      await axios.delete(`${API_URL}/api/goals/${goalId}`, { withCredentials: true });
      toast.success('Meta excluída');
      fetchGoals();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleUpdateValue = async (goalId, newValue) => {
    try {
      await axios.put(`${API_URL}/api/goals/${goalId}`, { current_value: parseFloat(newValue) }, { withCredentials: true });
      fetchGoals();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const getProgress = (goal) => {
    const target = goal.target_value || 1;
    const current = goal.current_value || 0;
    return Math.min(Math.round((current / target) * 100), 100);
  };

  const getProgressColor = (pct) => {
    if (pct >= 100) return '#0F1B3D';
    if (pct >= 60) return '#B8845C';
    if (pct >= 30) return '#E9C46A';
    return '#A8174E';
  };

  const overallProgress = goals.length > 0
    ? Math.round(goals.reduce((sum, g) => sum + getProgress(g), 0) / goals.length)
    : 0;

  const completedGoals = goals.filter(g => getProgress(g) >= 100).length;

  const formatMonthLabel = (m) => {
    const [y, mo] = m.split('-');
    const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    return `${months[parseInt(mo, 10) - 1]} ${y}`;
  };

  return (
    <div className="space-y-6" data-testid="goals-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Metas Mensais
          </h2>
          <p className="text-[#5F6B85]">{formatMonthLabel(currentMonth)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="month"
            value={currentMonth}
            onChange={e => setCurrentMonth(e.target.value)}
            className="w-[180px]"
            data-testid="goals-month-picker"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoUpdate}
            disabled={refreshing}
            className="border-[#E5DCC8]"
            data-testid="auto-update-goals"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="new-goal-button">
                <Plus className="h-4 w-4 mr-2" />
                Nova Meta
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Criar Meta</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <Select value={form.type} onValueChange={val => {
                    const gt = GOAL_TYPES.find(t => t.value === val);
                    setForm({ ...form, type: val, unit: gt?.unit || '', title: form.title || gt?.label || '' });
                  }}>
                    <SelectTrigger data-testid="goal-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GOAL_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Ex: Atingir R$ 10.000 em receita" data-testid="goal-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Valor Alvo *</Label>
                    <Input type="number" step="0.01" value={form.target_value} onChange={e => setForm({ ...form, target_value: e.target.value })} required data-testid="goal-target-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Unidade</Label>
                    <Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="R$, kg, etc" data-testid="goal-unit-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="goal-submit-button">
                  Criar Meta
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Overall Summary */}
      {goals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-[#E5DCC8]" data-testid="overall-progress-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#0F1B3D]/10 rounded-lg">
                  <Target className="h-5 w-5 text-[#0F1B3D]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#5F6B85]">Progresso Geral</p>
                  <p className="text-2xl font-bold text-[#0F1B3D]">{overallProgress}%</p>
                  <Progress value={overallProgress} className="mt-2 h-2" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DCC8]" data-testid="total-goals-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#B8845C]/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-[#B8845C]" />
                </div>
                <div>
                  <p className="text-sm text-[#5F6B85]">Total de Metas</p>
                  <p className="text-2xl font-bold text-[#B8845C]">{goals.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DCC8]" data-testid="completed-goals-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#0F1B3D]/10 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-[#0F1B3D]" />
                </div>
                <div>
                  <p className="text-sm text-[#5F6B85]">Metas Atingidas</p>
                  <p className="text-2xl font-bold text-[#0F1B3D]">{completedGoals}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Goal Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {goals.length === 0 ? (
          <Card className="border-[#E5DCC8] md:col-span-2">
            <CardContent className="p-8 text-center">
              <Target className="h-12 w-12 text-[#7A85A0] mx-auto mb-3" />
              <p className="text-[#7A85A0]">Nenhuma meta definida para este mês</p>
              <p className="text-sm text-[#7A85A0] mt-1">Crie suas metas para acompanhar o progresso da clínica</p>
            </CardContent>
          </Card>
        ) : (
          goals.map((goal) => {
            const pct = getProgress(goal);
            const color = getProgressColor(pct);
            return (
              <Card key={goal.goal_id} className="border-[#E5DCC8]" data-testid={`goal-card-${goal.goal_id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-[#0F1B3D]">{goal.title}</h3>
                      <p className="text-xs text-[#7A85A0] mt-0.5">{GOAL_TYPES.find(t => t.value === goal.type)?.label || goal.type}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(goal.goal_id)} className="text-[#A8174E] h-8 w-8 p-0" data-testid={`delete-goal-${goal.goal_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#5F6B85]">
                        {goal.current_value?.toFixed?.(1) || 0} / {goal.target_value} {goal.unit}
                      </span>
                      <span className="font-bold" style={{ color }}>{pct}%</span>
                    </div>
                    <div className="w-full bg-[#FAF6EE] rounded-full h-3">
                      <div
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                    {pct >= 100 && (
                      <div className="flex items-center gap-1 text-[#0F1B3D] text-sm font-medium">
                        <CheckCircle className="h-4 w-4" />
                        Meta atingida!
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
